import React, { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Calendar, FileText, Link as LinkIcon, AlertCircle, CheckCircle, Clock, Lock, Globe, Shield, Users, Eye, Download, Play, Pause } from 'lucide-react'
import { useResponsiveContext } from '../context/ResponsiveContext'
import { StatusBadge, TypeBadge, VisibilityBadge, Button } from './ui'

export default function MemoryCard({ memory, index, isArchiveMode, formatFileSize }) {
  const { shouldAnimate, isMobile, isTouch, hasHover } = useResponsiveContext()
  const [showPreview, setShowPreview] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentGateway, setCurrentGateway] = useState(0)

  // IPFS Gateway options for fallback
  const ipfsGateways = [
    'https://ipfs.io/ipfs/',
    'https://cloudflare-ipfs.com/ipfs/',
    'https://gateway.pinata.cloud/ipfs/',
    'https://dweb.link/ipfs/'
  ]

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
        return <Clock className="w-3 h-3" />
      case 'uploaded':
      case 'pinned':
      case 'moderated':
      case 'stored':
        return <CheckCircle className="w-3 h-3" />
      case 'collaborative':
        return <Users className="w-3 h-3" />
      case 'error':
      case 'rejected':
        return <AlertCircle className="w-3 h-3" />
      default:
        return <FileText className="w-3 h-3" />
    }
  }

  const getStatusClasses = (status) => {
    switch (status) {
      case 'uploading':
        return 'opacity-80 animate-pulse'
      case 'uploaded':
      case 'pinned':
      case 'moderated':
      case 'stored':
        return 'opacity-100'
      case 'collaborative':
        return 'opacity-100 ring-2 ring-blue-400/30'
      case 'error':
      case 'rejected':
        return 'opacity-70'
      default:
        return 'opacity-60'
    }
  }

  const getPreservationModeIcon = (mode) => {
    switch (mode) {
      case 'private':
        return <Lock className="w-3 h-3" />
      case 'public':
        return <Globe className="w-3 h-3" />
      case 'safekeeping':
        return <Shield className="w-3 h-3" />
      default:
        return <Lock className="w-3 h-3" />
    }
  }

  const getPreservationModeLabel = (mode) => {
    switch (mode) {
      case 'private':
        return 'Private'
      case 'public':
        return 'Public'
      case 'safekeeping':
        return 'Safe Keeping'
      default:
        return 'Private'
    }
  }

  // File type detection
  const getFileCategory = (fileName, type) => {
    const extension = fileName?.toLowerCase().split('.').pop()
    const mimeType = type?.toLowerCase()
    
    if (mimeType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
      return 'image'
    }
    if (mimeType?.startsWith('video/') || ['mp4', 'webm', 'ogg', 'avi', 'mov'].includes(extension)) {
      return 'video'
    }
    if (mimeType?.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(extension)) {
      return 'audio'
    }
    if (mimeType?.includes('pdf') || extension === 'pdf') {
      return 'pdf'
    }
    if (mimeType?.startsWith('text/') || ['txt', 'md', 'json', 'csv', 'log'].includes(extension)) {
      return 'text'
    }
    return 'other'
  }

  // Get preview URL with gateway fallback
  const getPreviewUrl = (gatewayIndex = currentGateway) => {
    let url = null
    let source = null
    let cid = null
    
    // Skip collaborative memories (they're session metadata, not files)
    if (memory.type === 'collaborative' || memory.userId === 'collaborative') {
      source = 'collaborative'
      console.log(`🔗 URL Generation for ${memory.id}:`, {
        source,
        url: null,
        reason: 'Collaborative memory - no preview available'
      })
      return null
    }
    
    // Determine which CID to use
    if (memory.cid) {
      cid = memory.cid
      source = 'cid'
    } else if (memory.localCid) {
      cid = memory.localCid
      source = 'localCid'
    }
    
    // Build URL with specified gateway
    if (cid) {
      const gateway = ipfsGateways[gatewayIndex] || ipfsGateways[0]
      url = `${gateway}${cid}`
    }
    // For public memories without direct CID access, try to construct URL from available data
    else if ((memory.preservationMode === 'public' || memory.visibility === 'public') && memory.id) {
      console.warn('🚨 Public memory missing CID, cannot preview:', {
        id: memory.id,
        preservationMode: memory.preservationMode,
        visibility: memory.visibility,
        userId: memory.userId,
        fileName: memory.fileName,
        availableFields: Object.keys(memory)
      })
      url = null
      source = 'missing'
    }
    
    // Debug log URL generation
    console.log(`🔗 URL Generation for ${memory.id}:`, {
      source,
      url,
      cid,
      gateway: ipfsGateways[gatewayIndex],
      gatewayIndex,
      fileName: memory.fileName,
      userId: memory.userId
    })
    
    return url
  }

  // Check if file can be previewed
  const canPreview = () => {
    // Collaborative memories are not previewable (they're session metadata, not files)
    if (memory.type === 'collaborative' || memory.userId === 'collaborative') {
      return false
    }
    
    const category = getFileCategory(memory.fileName, memory.type)
    const hasPreviewableType = ['image', 'video', 'audio', 'pdf', 'text'].includes(category)
    
    // Must have a valid preview URL to show preview
    const previewUrl = getPreviewUrl()
    
    // Debug logging for preview issues
    console.log(`🔍 Preview Debug for memory ${memory.id}:`, {
      fileName: memory.fileName,
      fileType: memory.type,
      category,
      hasPreviewableType,
      preservationMode: memory.preservationMode,
      visibility: memory.visibility,
      cid: memory.cid,
      localCid: memory.localCid,
      previewUrl,
      canPreview: hasPreviewableType && !!previewUrl,
      userId: memory.userId,
      status: memory.status,
      isArchiveMode,
      isCollaborative: memory.type === 'collaborative' || memory.userId === 'collaborative',
      memoryStructure: Object.keys(memory)
    })
    
    return hasPreviewableType && !!previewUrl
  }

  // Toggle preview
  const togglePreview = useCallback(() => {
    if (!canPreview()) return
    
    console.log('🎯 Toggle Preview:', {
      currentState: showPreview,
      newState: !showPreview,
      memoryId: memory.id,
      fileName: memory.fileName,
      hasPreviewUrl: !!getPreviewUrl()
    })
    
    setShowPreview(!showPreview)
    setPreviewError(null)
    
    // Reset to first gateway and set loading when opening preview
    if (!showPreview) {
      setCurrentGateway(0)
      setPreviewLoading(true)
      console.log('🔄 Opening preview, setting loading state')
    } else {
      setPreviewLoading(false)
      console.log('🔄 Closing preview, clearing loading state')
    }
  }, [showPreview, memory.cid, memory.id, memory.fileName])

  // Handle preview load error with gateway fallback
  const handlePreviewError = useCallback((event) => {
    const previewUrl = getPreviewUrl()
    
    // Enhanced error logging for debugging
    console.error('🚨 Preview Load Error - Detailed Analysis:', {
      memoryId: memory.id,
      fileName: memory.fileName,
      previewUrl,
      currentGateway,
      gatewayUrl: ipfsGateways[currentGateway],
      cid: memory.cid,
      localCid: memory.localCid,
      userId: memory.userId,
      preservationMode: memory.preservationMode,
      visibility: memory.visibility,
      type: memory.type,
      status: memory.status,
      error: event?.target?.error || event?.error || 'Unknown error',
      eventType: event?.type,
      target: event?.target?.tagName,
      networkError: event?.target?.networkError,
      httpStatus: event?.target?.status,
      responseURL: event?.target?.responseURL,
      timestamp: new Date().toISOString(),
      // Test CID validity
      cidValid: memory.cid && memory.cid.length > 40,
      cidFormat: memory.cid ? (memory.cid.startsWith('Qm') ? 'v0' : 'v1') : 'none',
      fullMemoryStructure: memory
    })
    
    // Try next gateway if available
    if (currentGateway < ipfsGateways.length - 1) {
      console.log(`🔄 Gateway Fallback (${currentGateway + 1}/${ipfsGateways.length}):`, {
        from: ipfsGateways[currentGateway],
        to: ipfsGateways[currentGateway + 1],
        cid: memory.cid,
        fullUrl: `${ipfsGateways[currentGateway + 1]}${memory.cid}`
      })
      setCurrentGateway(currentGateway + 1)
      setPreviewLoading(true)
      setPreviewError(null)
    } else {
      console.error('❌ All IPFS gateways exhausted:', {
        memoryId: memory.id,
        fileName: memory.fileName,
        cid: memory.cid,
        gateways: ipfsGateways,
        finalError: event?.target?.error || 'Network timeout'
      })
      setPreviewError(`Failed to load file preview from all gateways (${memory.fileName})`)
      setPreviewLoading(false)
    }
  }, [memory, currentGateway, ipfsGateways])

  // Test IPFS URL in new tab for debugging
  const testIPFSUrl = useCallback(() => {
    const previewUrl = getPreviewUrl()
    if (previewUrl) {
      console.log('🔗 Manual IPFS URL Test:', {
        url: previewUrl,
        cid: memory.cid,
        gateway: ipfsGateways[currentGateway],
        memoryId: memory.id,
        fileName: memory.fileName
      })
      window.open(previewUrl, '_blank')
    } else {
      console.warn('❌ No IPFS URL available to test for memory:', memory.id)
    }
  }, [memory, currentGateway, ipfsGateways])

  // Handle preview load success
  const handlePreviewLoad = useCallback(() => {
    console.log('✅ Preview Load Success:', {
      memoryId: memory.id,
      fileName: memory.fileName,
      previewUrl: getPreviewUrl(),
      cid: memory.cid,
      localCid: memory.localCid,
      userId: memory.userId
    })
    
    setPreviewLoading(false)
    setPreviewError(null)
  }, [memory.id, memory.fileName, memory.cid, memory.localCid, memory.userId])

  return (
    <motion.div
      className={`
        memory-card w-full max-w-full
        ${isArchiveMode ? 'bg-archive-card text-archive-text' : ''}
        group relative overflow-hidden
        transition-all duration-500 ease-luxury
        hover:shadow-2xl hover:z-20
        ${hasHover ? 'hover:scale-105' : ''}
      `}
      initial={shouldAnimate ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }}
      animate={{ opacity: 1, y: 0 }}
      transition={shouldAnimate ? {
        duration: isMobile ? 0.4 : 0.6,
        delay: Math.min(index * (isMobile ? 0.05 : 0.1), 0.5),
        ease: [0.33, 1, 0.68, 1]
      } : {}}
      whileHover={hasHover && shouldAnimate ? { 
        y: -12, 
        scale: 1.02,
        transition: { duration: 0.3, ease: [0.33, 1, 0.68, 1] }
      } : {}}
      whileTap={isTouch ? { scale: 0.98 } : {}}
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-2">
          <TypeBadge type={memory.type} size="sm" />
          {/* Preservation Mode Badge */}
          <div className="flex items-center gap-1 px-2 py-1 bg-current/10 rounded-full text-xs font-medium">
            {getPreservationModeIcon(memory.preservationMode || 'private')}
            <span className="uppercase tracking-wider">
              {getPreservationModeLabel(memory.preservationMode || 'private')}
            </span>
          </div>
          {memory.visibility === 'public' && (
            <VisibilityBadge visibility="public" size="sm" />
          )}
        </div>

        <div className="flex items-center gap-2">
          <StatusBadge
            status={memory.status}
            className={`
              text-xs ${getStatusClasses(memory.status)}
              hover:scale-105 motion-smooth
            `}
          />
          {memory.pinned && <span className="text-lg">📌</span>}
        </div>
      </div>

      {/* Memory Note - Enhanced for desktop */}
      <div className="mb-4">
        <blockquote className="text-base leading-relaxed font-normal tracking-tight opacity-90 italic group-hover:opacity-100 transition-opacity duration-300">
          "{memory.note}"
        </blockquote>
      </div>

      {/* File Preview Toggle */}
      {canPreview() && (
        <div className="mb-4">
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={togglePreview}
              className="flex-1 justify-center text-xs hover:bg-current/10 transition-all duration-300"
            >
              <Eye className="w-3 h-3 mr-1" />
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </Button>
            
            {/* Debug: Test IPFS link directly */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const url = getPreviewUrl()
                console.log('🧪 Testing IPFS link directly:', {
                  memoryId: memory.id,
                  url,
                  fileName: memory.fileName,
                  userId: memory.userId
                })
                if (url) {
                  window.open(url, '_blank')
                } else {
                  console.error('No URL available for testing')
                }
              }}
              className="px-3 text-xs hover:bg-current/10 transition-all duration-300"
              title="Test IPFS link in new tab"
            >
              🧪
            </Button>
          </div>
        </div>
      )}

      {/* File Preview */}
      {showPreview && canPreview() && (
        <motion.div
          className="mb-4 border border-current/20 rounded-lg overflow-hidden bg-current/5"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          {previewLoading && (
            <div className="flex items-center justify-center p-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-current"></div>
              <div className="ml-2">
                <div className="text-sm">Loading preview...</div>
                <div className="text-xs opacity-60">
                  {getPreviewUrl()?.substring(0, 50)}...
                </div>
              </div>
            </div>
          )}

          {previewError && (
            <div className="flex items-center justify-center p-4 text-red-500">
              <AlertCircle className="w-4 h-4 mr-2" />
              <span className="text-sm">{previewError}</span>
            </div>
          )}

          {!previewLoading && !previewError && (() => {
            const category = getFileCategory(memory.fileName, memory.type)
            const previewUrl = getPreviewUrl()
            
            console.log('🎨 Rendering preview:', {
              category,
              previewUrl,
              fileName: memory.fileName,
              memoryId: memory.id
            })
            
            switch (category) {
              case 'image':
                return (
                  <div className="relative">
                    <img
                      src={previewUrl}
                      alt={memory.fileName}
                      className="w-full max-h-64 object-contain"
                      onLoad={(e) => {
                        console.log('🖼️ Image loaded successfully:', {
                          src: e.target.src,
                          naturalWidth: e.target.naturalWidth,
                          naturalHeight: e.target.naturalHeight,
                          memoryId: memory.id
                        })
                        handlePreviewLoad(e)
                      }}
                      onError={(e) => {
                        console.error('🖼️ Image failed to load:', {
                          src: e.target.src,
                          error: e.target.error,
                          memoryId: memory.id
                        })
                        handlePreviewError(e)
                      }}
                      loading="lazy"
                      style={{ maxHeight: '256px', width: '100%', objectFit: 'contain' }}
                    />
                    {previewLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-current/10">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-current"></div>
                      </div>
                    )}
                  </div>
                )
              
              case 'video':
                return (
                  <video
                    src={previewUrl}
                    controls
                    className="w-full max-h-64"
                    onLoadedData={handlePreviewLoad}
                    onError={handlePreviewError}
                    preload="metadata"
                  >
                    Your browser does not support video playback.
                  </video>
                )
              
              case 'audio':
                return (
                  <div className="p-4">
                    <audio
                      src={previewUrl}
                      controls
                      className="w-full"
                      onLoadedData={handlePreviewLoad}
                      onError={handlePreviewError}
                      preload="metadata"
                    >
                      Your browser does not support audio playback.
                    </audio>
                  </div>
                )
              
              case 'pdf':
                return (
                  <div className="p-4">
                    <div className="text-center text-sm opacity-70 mb-2">
                      PDF Preview
                    </div>
                    <iframe
                      src={`${previewUrl}#view=FitH`}
                      className="w-full h-64 border-0"
                      onLoad={handlePreviewLoad}
                      onError={handlePreviewError}
                      title={memory.fileName}
                    />
                  </div>
                )
              
              case 'text':
                return (
                  <div className="p-4">
                    <div className="text-center text-sm opacity-70 mb-2">
                      Text Preview
                    </div>
                    <iframe
                      src={previewUrl}
                      className="w-full h-32 border-0 bg-white text-black rounded"
                      onLoad={handlePreviewLoad}
                      onError={handlePreviewError}
                      title={memory.fileName}
                      style={{ fontSize: '12px' }}
                    />
                  </div>
                )
              
              default:
                return (
                  <div className="p-4 text-center text-sm opacity-70">
                    Preview not available for this file type
                  </div>
                )
            }
          })()}
        </motion.div>
      )}

      {/* Collaborative Users Display */}
      {memory.isCollaborative && memory.activeUsers && memory.activeUsers.length > 0 && (
        <div className="mb-4 flex items-center gap-2 text-sm text-current/70">
          <Users className="w-4 h-4" />
          <span>{memory.activeUsers.length} user{memory.activeUsers.length !== 1 ? 's' : ''} collaborating</span>
          <div className="flex -space-x-1 ml-2">
            {memory.activeUsers.slice(0, 3).map((user, userIndex) => (
              <div
                key={user.userId || userIndex}
                className="w-5 h-5 rounded-full border border-current/20 flex items-center justify-center text-xs font-medium text-white text-shadow"
                style={{ backgroundColor: user.color || '#6B7280' }}
                title={user.userName || 'Anonymous User'}
              >
                {(user.userName || 'A').charAt(0).toUpperCase()}
              </div>
            ))}
            {memory.activeUsers.length > 3 && (
              <div className="w-5 h-5 rounded-full bg-current/20 border border-current/20 flex items-center justify-center text-xs">
                +{memory.activeUsers.length - 3}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Display */}
      {memory.error && (
        <StatusBadge
          status="error"
          className="w-full mb-4 p-3 justify-start"
        >
          <AlertCircle className="w-4 h-4 mr-2" />
          {memory.error}
        </StatusBadge>
      )}

      {/* Metadata */}
      <div className="mt-4 pt-4 border-t border-current/20">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-xs opacity-70">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {new Date(memory.timestamp).toLocaleDateString()}
            </span>
            <span className="flex items-center gap-1">
              <FileText className="w-3 h-3" />
              {formatFileSize(memory.fileSize)}
            </span>
          </div>
          <div className="text-xs font-mono opacity-60">
            {memory.fileName}
          </div>
        </div>
      </div>

      {/* Storage Information */}
      <motion.div
        className="mt-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        {memory.preservationMode === 'private' && (
          <StatusBadge
            status="stored"
            className="w-full justify-center"
          >
            <Lock className="w-3 h-3 mr-1" />
            Stored locally in IndexedDB
            {!memory.cid && (
              <span className="text-xs ml-2 opacity-70">
                (No preview available)
              </span>
            )}
          </StatusBadge>
        )}

        {/* Show IPFS links for public/safekeeping memories with CIDs */}
        {(memory.cid || memory.localCid) && (memory.preservationMode === 'public' || memory.preservationMode === 'safekeeping') && (
          <>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 justify-center font-mono text-xs group-hover:bg-current/10 transition-all duration-300 hover:scale-105"
                asChild
              >
                <a
                  href={`https://ipfs.io/ipfs/${memory.cid || memory.localCid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View on IPFS"
                  className="focus:outline-none focus:ring-2 focus:ring-current focus:ring-opacity-50 rounded-lg"
                >
                  <LinkIcon className="w-3 h-3 group-hover:rotate-12 transition-transform duration-300" />
                  <span className="truncate max-w-[120px]">
                    {(memory.cid || memory.localCid).substring(0, 12)}...
                  </span>
                </a>
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                className="px-3 hover:bg-current/10 transition-all duration-300 hover:scale-105"
                asChild
              >
                <a
                  href={`https://ipfs.io/ipfs/${memory.cid || memory.localCid}`}
                  download={memory.fileName}
                  title="Download file"
                  className="focus:outline-none focus:ring-2 focus:ring-current focus:ring-opacity-50 rounded-lg"
                >
                  <Download className="w-3 h-3" />
                </a>
              </Button>
            </div>

            {memory.proof && memory.proof.pinned && (
              <StatusBadge
                status="uploaded"
                className="w-full mt-2 justify-center"
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                {memory.preservationMode === 'safekeeping' ? 'IPFS sharable' : 'Permanently preserved'}
              </StatusBadge>
            )}
          </>
        )}

        {/* Warning for public memories missing CID */}
        {!memory.cid && !memory.localCid && (memory.preservationMode === 'public' || memory.visibility === 'public') && (
          <StatusBadge
            status="error"
            className="w-full justify-center"
          >
            <AlertCircle className="w-3 h-3 mr-1" />
            Public memory missing IPFS link
          </StatusBadge>
        )}
      </motion.div>
    </motion.div>
  )
}